import React, { useEffect, useState } from 'react'
import { View, ActivityIndicator, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { IconButton } from '@mui/material'
import { SearchRounded, FilterList } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { useStores, setPageTitle, FilterTermsProps } from '@trivie/core'
import { color } from '@trivie/ui-web/theme'
import { Header, TabController } from '@trivie/ui-mobile'
import { SubscriptionLockedScreen } from '@trivie/ui-mobile/components/subscription-locked-screen'

import { TopicsTab } from './tabs'
import { QuizzesTab } from './tabs/quizzes-tab'
import { ResourcesTab } from './tabs/resources-tab'
import { SubscriptionsTab } from './tabs/subscriptions-tab'
import { FilterLibraryModal } from './library-filter-modal'
import { PathsTab } from './tabs/paths-tab'
import { NullState } from './null-state'
import { SurveysTab } from './tabs/surveys-tab'

export const LibraryScreen = observer(() => {
  const { surveyStore, libraryStore, gameStore, userStore, pathStore, uiStore, subscriptionStore } =
    useStores()
  const { assignedSurveys, getAssignedSurveysForUser } = surveyStore
  const { setQuiz } = gameStore
  const { userPaths, getPathsForUser } = pathStore
  const {
    getTopicsForUser,
    getResourcesForUser,
    getQuizzesForUser,
    quizzes,
    allResources,
    topicsSansGeneral,
    isLoadingQuizzes,
    isLoadingResources,
    isLoadingTopics,
  } = libraryStore
  const { user, getBranding, enterpriseTier } = userStore

  const [activeTab, setActiveTab] = useState(
    !enterpriseTier ? 2 : !user?.workspace_settings?.learning_paths_enabled ? 1 : 0,
  )

  const [searchTerm, setSearchTerm] = useState('')
  const [showFilterModal, setShowFilterModal] = useState(false)

  const [filterPathTerms, setFilterPathTerms] = useState<FilterTermsProps>({
    status: [],
    sources: [],
  })

  const [filterTopicTerms, setFilterTopicTerms] = useState<FilterTermsProps>({
    status: [],
    sources: [],
  })

  const [filterQuizTerms, setFilterQuizTerms] = useState<FilterTermsProps>({
    status: [],
    sources: [],
  })

  const [filterResourceTerms, setFilterResourceTerms] = useState<FilterTermsProps>({
    status: [],
    sources: [],
  })

  const { t } = useTranslation()

  const loadData = () => {
    if (user) {
      getBranding(user.id)
      if (userStore.isSubscriptionWorkspace) {
        subscriptionStore.getSubscriptionsForUser(user.id)
      }
      if (enterpriseTier && user.workspace_settings?.learning_paths_enabled) {
        Promise.all([
          getAssignedSurveysForUser(user.id),
          getPathsForUser(user.id),
          getTopicsForUser(user.id),
          getQuizzesForUser(user.id),
          getResourcesForUser(user.id),
        ])
      } else if (enterpriseTier) {
        Promise.all([
          getAssignedSurveysForUser(user.id),
          getTopicsForUser(user.id),
          getQuizzesForUser(user.id),
          getResourcesForUser(user.id),
        ])
      } else {
        Promise.all([getQuizzesForUser(user.id), getResourcesForUser(user.id)])
      }
    }
  }

  useEffect(() => {
    setPageTitle(`Library - ${process.env.REACT_APP_SITE_TITLE}`)
    loadData()
  }, [])

  useEffect(() => {
    setQuiz(undefined)
    if (userStore.isSubscriptionWorkspace) {
      setActiveTab(0)
    } else if (
      user?.workspace_settings?.learning_paths_enabled &&
      userPaths?.length > 0 &&
      enterpriseTier
    ) {
      setActiveTab(1)
    } else if (topicsSansGeneral?.length > 0 && enterpriseTier) {
      setActiveTab(2)
    } else if (quizzes?.length > 0) {
      setActiveTab(3)
    } else if (allResources?.length > 0) {
      setActiveTab(4)
    }
  }, [
    userPaths?.length,
    topicsSansGeneral?.length,
    quizzes?.length,
    allResources?.length,
    enterpriseTier,
    subscriptionStore.userSubscriptions?.length,
  ])

  const tabs = [
    {
      id: 0,
      name: 'subscriptions',
      label: 'Library.subscriptions',
      component: <SubscriptionsTab searchTerm={searchTerm} />,
      onPress: () => setActiveTab(0),
      numeral: String(subscriptionStore.userSubscriptions?.length ?? 0),
      enable: userStore.isSubscriptionWorkspace,
    },
    {
      id: 1,
      name: 'paths',
      label: 'Library.paths',
      component: (
        <PathsTab
          clearFilters={() => setFilterPathTerms({ status: [], sources: [] })}
          searchTerm={searchTerm}
          filterTerms={filterPathTerms}
        />
      ),
      onPress: () => setActiveTab(1),
      numeral: String(userPaths?.length ?? 0),
      enable: Boolean(
        enterpriseTier && userPaths?.length > 0 && user?.workspace_settings?.learning_paths_enabled,
      ),
    },
    {
      id: 2,
      name: 'topics',
      label: 'Library.topics',
      component: (
        <TopicsTab
          clearFilters={() => setFilterTopicTerms({ status: [], sources: [] })}
          searchTerm={searchTerm}
          filterTerms={filterTopicTerms}
        />
      ),
      onPress: () => setActiveTab(2),
      numeral: String(topicsSansGeneral?.length ?? 0),
      enable: enterpriseTier && topicsSansGeneral?.length > 0,
    },
    {
      id: 3,
      name: 'quizzes',
      label: 'Library.quizzes',
      component: (
        <QuizzesTab
          clearFilters={() => setFilterQuizTerms({ status: [], sources: [] })}
          searchTerm={searchTerm}
          filterTerms={filterQuizTerms}
        />
      ),
      onPress: () => setActiveTab(3),
      enable: quizzes?.length > 0,
      numeral: String(quizzes?.length ?? 0),
    },
    {
      id: 4,
      name: 'resources',
      label: 'Library.resources',
      component: (
        <ResourcesTab
          clearFilters={() => setFilterResourceTerms({ status: [], sources: [] })}
          searchTerm={searchTerm}
          filterTerms={filterResourceTerms}
        />
      ),
      onPress: () => setActiveTab(4),
      numeral: String(allResources?.length ?? 0),
      enable: allResources?.length > 0,
    },
    {
      id: 5,
      name: 'surveys',
      label: 'Survey.surveys',
      component: <SurveysTab searchTerm={searchTerm} />,
      onPress: () => setActiveTab(5),
      numeral: String(assignedSurveys?.length ?? 0),
      enable: assignedSurveys?.length > 0,
    },
  ]

  const hasContent = Boolean(
    assignedSurveys?.length ||
      subscriptionStore.userSubscriptions?.length ||
      userPaths?.length ||
      topicsSansGeneral?.length ||
      quizzes?.length ||
      allResources?.length,
  )

  const isSubscriptionWorkspace = userStore.isSubscriptionWorkspace
  const hasActive = subscriptionStore.hasActiveSubscriptions

  const loading =
    surveyStore.isLoading ||
    isLoadingQuizzes ||
    isLoadingResources ||
    isLoadingTopics ||
    pathStore.loading ||
    subscriptionStore.isLoadingSubscriptions

  if (isSubscriptionWorkspace && !hasActive && !hasContent && !loading) {
    return (
      <div style={ROOT}>
        <div
          style={{
            ...CONTAINER,
            flexDirection: 'column',
            justifyContent: 'center',
            display: 'flex',
          }}
        >
          <div style={HEADER_STICKY_CONTAINER}>
            <div style={HEADER_FLEX_CONTAINER}>
              <div style={HEADER_ITEM1}>
                <Header
                  titleStyle={{ fontSize: 24, color: color.black }}
                  headerTx="Library.header"
                />
              </div>
            </div>
          </div>
          <SubscriptionLockedScreen
            emoji="📚"
            title="No active subscriptions"
            helperText="Purchase or renew a subscription to start learning"
            buttonText="Browse Products"
            onButtonPress={
              userStore.externalStoreUrl
                ? () => {
                    window.open(
                      // @ts-ignore
                      userStore.externalStoreUrl,
                      '_blank',
                      'noopener',
                    )
                  }
                : undefined
            }
          />
        </div>
      </div>
    )
  }

  return (
    <div style={ROOT}>
      <div style={HEADER_STICKY_CONTAINER}>
        <div style={HEADER_FLEX_CONTAINER}>
          <div style={HEADER_ITEM1}>
            <Header titleStyle={{ fontSize: 24, color: color.black }} headerTx="Library.header" />
          </div>
        </div>
      </div>
      {loading ? (
        <div style={LOADER_CONTAINER}>
          <ActivityIndicator style={LOADER} size="small" />
        </div>
      ) : hasContent ? (
        <>
          <div style={SEARCH_POPUP_CONTAINER}>
            <SearchRounded style={SEARCH_ICON} />
            <input
              type="text"
              placeholder={t('Common.search')}
              style={{ ...SEARCH_INPUT, fontFamily: uiStore.branding.font_family }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <IconButton
              disabled={activeTab === 0}
              aria-label="Close"
              onClick={() => setShowFilterModal(true)}
              size="large"
            >
              <FilterList
                htmlColor={activeTab === 0 ? color.transparent : color.black}
                style={{ fontSize: 22 }}
              />
              {activeTab === 0 && filterPathTerms.status.length > 0 ? (
                <View style={FILTER_INDICATOR} />
              ) : null}
              {activeTab === 1 && filterTopicTerms.status.length > 0 ? (
                <View style={FILTER_INDICATOR} />
              ) : null}
              {activeTab === 2 && filterQuizTerms.status.length > 0 ? (
                <View style={FILTER_INDICATOR} />
              ) : null}
              {activeTab === 3 && filterResourceTerms.sources.length > 0 ? (
                <View style={FILTER_INDICATOR} />
              ) : null}
            </IconButton>
          </div>
          <div style={CONTAINER}>
            <TabController
              matchID
              containerStyle={{
                marginBottom: 20,
                justifyContent: 'flex-start',
                paddingHorizontal: 0,
              }}
              setActiveTab={setActiveTab}
              activeTab={activeTab}
              tabs={tabs}
            />
          </div>
          {activeTab === 1 && (
            <FilterLibraryModal
              setShowModal={setShowFilterModal}
              filterTerms={filterPathTerms}
              open={showFilterModal}
              isStatusFilter
              title="Filter Paths"
              setFilterTerms={setFilterPathTerms}
            />
          )}
          {activeTab === 2 && (
            <FilterLibraryModal
              setShowModal={setShowFilterModal}
              filterTerms={filterTopicTerms}
              open={showFilterModal}
              isStatusFilter
              title="Filter Topics"
              setFilterTerms={setFilterTopicTerms}
            />
          )}
          {activeTab === 3 && (
            <FilterLibraryModal
              setShowModal={setShowFilterModal}
              filterTerms={filterQuizTerms}
              open={showFilterModal}
              isStatusFilter
              title="Filter Quizzes"
              setFilterTerms={setFilterQuizTerms}
            />
          )}
          {activeTab === 4 && (
            <FilterLibraryModal
              setShowModal={setShowFilterModal}
              filterTerms={filterResourceTerms}
              open={showFilterModal}
              isStatusFilter={false}
              title="Filter Resources"
              setFilterTerms={setFilterResourceTerms}
            />
          )}
        </>
      ) : (
        <NullState />
      )}
    </div>
  )
})

/**
 * Styling
 */
const ROOT: React.CSSProperties = { height: '100%' }
const CONTAINER: React.CSSProperties = {
  height: '100%',
}
const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}

const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  justifyContent: 'space-between',
}

const HEADER_ITEM1: React.CSSProperties = {}
const HEADER_ITEM2: React.CSSProperties = {
  marginTop: 20,
  height: '100%',
}
const SEARCH_ICON: React.CSSProperties = {
  position: 'relative',
  left: 36,
  color: color.shade30,
  fontSize: 24,
  alignSelf: 'center',
}

const SEARCH_POPUP_CONTAINER: React.CSSProperties = {
  display: 'flex',
  position: 'sticky',
  marginTop: 15,
  marginBottom: 15,
  marginLeft: -25,
}
const SEARCH_INPUT: React.CSSProperties = {
  fontSize: 17,
  padding: 8,
  paddingLeft: 40,
  marginRight: 8,
  flex: 1,
  borderRadius: 40,
  backgroundColor: 'rgb(245,245,245)',
  borderWidth: 0,
  borderColor: color.grey,
  borderTopColor: color.grey,
}
const FILTER_INDICATOR: ViewStyle = {
  height: 8,
  width: 8,
  borderRadius: 100,
  position: 'absolute',
  backgroundColor: color.blue,
  zIndex: 999,
  right: 10,
  top: 27,
}

const LOADER_CONTAINER: React.CSSProperties = {
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  position: 'relative',
}
const LOADER: ViewStyle = {
  flex: 1,
  alignSelf: 'center',
  position: 'relative',
  height: window.innerHeight / 2,
  bottom: 100,
}
